import { Component, OnInit, OnDestroy, inject, DOCUMENT, effect } from '@angular/core';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType } from '@azure/msal-browser';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router, RouterOutlet } from "@angular/router";
import { HeaderComponent } from "./pages/header/header.component";
import { PRIMENG_MODULES } from './shared/primeng-imports';
import { MenuItem } from 'primeng/api';
import { AuthService } from './shared/services/auth.service';
import { DataService } from './shared/services/data.service';
import { PrintViewComponent } from "./shared/components/print-view/print-view.component";
import { environment } from '../environments/environment';
import { LeftNavigationMenuComponent } from './shared/components/left-navigation-menu/left-navigation-menu.component';

@Component({
  selector: 'app-root',
  standalone: true,
  styleUrls: ['app.component.scss'],
  templateUrl: 'app.component.html',
  imports: [RouterOutlet, HeaderComponent, ...PRIMENG_MODULES, PrintViewComponent, LeftNavigationMenuComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly superUserGroupPrefix = 'DIA-SuperUser-';
  private readonly document = inject(DOCUMENT);
  isLoggedIn = false;
  isInitialized = false;
  userName: string = '';
  userDisplayName: string = '';
  private readonly _destroying$ = new Subject<void>();
  items: MenuItem[] | undefined;
  tabLength = 0;
  isCustomsGroup: boolean = false;
  isAdminGroup = false;
  isSuperUser: boolean = false;
  showtabs = true;
  showInactiveDialog: boolean = false;
  isActiveUserDialogMsg: string = 'Error';
  isReload: boolean = false;
  userStatus$!: Observable<{ isActive: boolean; message: string }>;
  normalUserGroupPrefix: string = 'DIA-NormalUser-';
  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userAuthService: AuthService,
    private router: Router,
    private dataService: DataService

  ) {
    effect(() => {
      this.showtabs = this.dataService.showMaintabsSignal();
    });
  }

  async ngOnInit() {
    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'assets/swiper-bundle.min.css';
    this.document.head.appendChild(link);
    try {
      // 1. Initialize MSAL
      await this.authService.instance.initialize();

      // 2. IMPORTANT: Handle the redirect results
      const result = await this.authService.instance.handleRedirectPromise();
      //If we aren't returning from a redirect , manually check the cache 
      if (!result) {
        const currentAccount = this.authService.instance.getAllAccounts();

        if (currentAccount.length > 0) {
          this.isReload = true;
          this.authService.instance.setActiveAccount(currentAccount[0]);

        }
      } else {
        this.isReload = false;
        this.authService.instance.setActiveAccount(result.account);

      }


      this.checkAccount(); // Initial check
    } catch (error) {
      console.error("Init error:", error);
    }

    // 3. Listen for successful login events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAccount();
      });

    // 4. Update UI when interaction stops
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.router.initialNavigation()
        this.checkAccount();
      });
  }

  checkAccount() {
    const accounts = this.authService.instance.getAllAccounts();
    this.isLoggedIn = accounts.length > 0;

    if (this.isLoggedIn) {
      this.authService.instance.setActiveAccount(accounts[0]);
      this.userName = accounts[0].username;      
      this.userDisplayName = this.formatDisplayName((accounts[0].idTokenClaims as { name?: string })?.name);
      this.getUserInfo();
      this.loadTabs();
      // this.redirectRetrievalRequestsUser();
    } else {
      this.router.navigate(['/Auth']);
    }
  }

  private formatDisplayName(name?: string): string {
    if (!name) {
      return '';
    }

    const [lastName, firstName] = name.split(',').map(part => part.trim());
    return firstName ? `${firstName} ${lastName}` : lastName;
  }

  private isRetrievalRequestsUser(): boolean {
    const isSuperUser = this.userAuthService.isInGroup([this.superUserGroupPrefix]);
    const isNormalUser = this.userAuthService.isInGroup([this.normalUserGroupPrefix]);

    return isSuperUser && isNormalUser;
  }

  // private redirectRetrievalRequestsUser() {
  //   if (this.isRetrievalRequestsUser() && this.router.url !== '/RetrievalRequests') {
  //     this.router.navigate(['/RetrievalRequests']);
  //   }
  // }


  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
  loadTabs() {
    const userGroup: any[] = this.userAuthService.getUserGroups();
    const showRetrievalRequestsTab = this.isRetrievalRequestsUser();
    this.dataService.updateUserGrps(userGroup);

    this.tabLength = userGroup.length;
    const matchForNormalUser = userGroup.filter(grp => grp.includes('DIA-NormalUser-'));
    const matchForCustomUser = userGroup.filter(grp => grp.includes('DIA-CustomsUser-'));
    const matchForAdmin = userGroup.filter(grp => grp.includes('DIA-CustomsAdminUser-'));
    this.isSuperUser = (userGroup.filter(grp => grp.includes('DIA-SuperUser-'))).length > 0 ? true : false;
    this.isAdminGroup = matchForAdmin.length > 0 ? true : false;
    this.isCustomsGroup = matchForCustomUser.length > 0 ? true : false;
    this.items = [
      ...((this.isAdminGroup || this.isCustomsGroup) ? [
        {
          label: 'Assigned To Me',
          icon: 'pi pi-user',
          routerLink: '/AssignedToMe',
        },
        {
          label: 'Unassigned',
          icon: 'pi pi-inbox',
          routerLink: '/Unassigned',
        },
        {
          label: 'Reprocessing',
          icon: 'pi pi-refresh',
          routerLink: '/Reprocessing',
        },
        {
          label: 'Firma',
          icon: 'pi pi-file-edit',
          routerLink: '/Firma',
        },
        // {
        //   label: 'Search Parcel',
        //   icon: 'pi pi-search',
        //   routerLink: '/SearchParcel',
        // },
        {
          label: 'All Assigned',
          icon: 'pi pi-list-check',
          routerLink: '/Allassigned',
        },
        // {
        //   label: 'Terminal',
        //   icon: 'pi pi-desktop',
        //   routerLink: '/Terminal',
        // },
        {
          label: 'Search',
          icon: 'pi pi-search-plus',
          routerLink: '/Search',
        },
      ] : (matchForNormalUser.length > 0) ? [
        // {
        //   label: 'Search Parcel',
        //   icon: 'pi pi-search',
        //   routerLink: '/SearchParcel',
        // },
        // {
        //   label: 'Terminal',
        //   icon: 'pi pi-desktop',
        //   routerLink: '/Terminal',
        // },
        {
          label: 'Search',
          icon: 'pi pi-search-plus',
          routerLink: '/Search',
        },
      ] : []),
      ...(showRetrievalRequestsTab ? [{
        label: 'Retrieval Requests',
        icon: 'pi pi-history',
        routerLink: '/RetrievalRequests',
      }] : []),
    ];
    if (userGroup.length > 0) {
      this.isInitialized = true;
    }
    if (userGroup.length > 0 && !this.isReload) {
      this.checkUserStatus();
    } else if (userGroup.length == 0) {
      this.showNoGroupFoundMsgDialog()
    } else {
      this.checkUserStatus();
    }
  }
  showNoGroupFoundMsgDialog() {
    this.isActiveUserDialogMsg = "You do not have sufficient permissions to access DIA application.";
    this.showInactiveDialog = true;
  }
  cancelDialog() {
    this.showInactiveDialog = false;
    this.logout();
  }
  getUserInfo() {
    let loggedInAccount: any = this.authService.instance.getAllAccounts()[0];
    if (loggedInAccount?.username)
      this.dataService.updateData(loggedInAccount.username);

  }
  checkUserStatus() {
    this.userStatus$ = this.dataService.isUserActive(this.userAuthService.fetchLoginUserName());
    this.userStatus$.subscribe(status => {

      this.showInactiveDialog = !status.isActive;
      this.isActiveUserDialogMsg = status.message;
    })
  }
}
